import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';

/** Bare, wrapping text follows the same inline editing model as question labels. */
export function InlineSectionText({ value, onChange, label, className = '', placeholder }: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  className?: string;
  placeholder?: string;
}): React.ReactElement {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const resize = () => {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value]);
  return <textarea ref={ref} rows={1} aria-label={label} value={value} placeholder={placeholder}
    className={`inline-section-text ${className}`} onChange={event => onChange(event.target.value)} />;
}

/** Inline metadata and the existing field cards belong to a single section. */
export interface SectionFrameProps {
  id: string;
  tone?: 'volt' | 'warmth' | 'flow';
  title: string;
  description?: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  readOnly?: boolean;
  onTitleChange?: (value: string) => void;
  onDescriptionChange?: (value: string) => void;
  beforeLayout: () => void;
  afterLayout: () => void;
  actions?: React.ReactNode;
  /** A section gate remains discoverable without opening a question. */
  conditions?: React.ReactNode;
  sectionRef?: React.Ref<HTMLElement>;
  dropTargetId?: string;
  dropActive?: boolean;
  dragging?: boolean;
  dragHandle?: React.ReactNode;
  dropIndicator?: React.ReactNode;
  headingDropIndicator?: React.ReactNode;
  children: React.ReactNode;
}

/** Inline section shell for the conversation builder. */
export function SectionFrame(props: SectionFrameProps): React.ReactElement {
  const [inLayout, setInLayout] = useState(props.expanded);
  const [visible, setVisible] = useState(props.expanded);
  const { beforeLayout, afterLayout, expanded } = props;
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let frame = 0;
    let timer = 0;
    if (expanded) {
      beforeLayout();
      setInLayout(true);
      frame = window.requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      timer = window.setTimeout(() => { beforeLayout(); setInLayout(false); }, reduced ? 0 : 200);
    }
    return () => { window.cancelAnimationFrame(frame); window.clearTimeout(timer); };
  }, [expanded, beforeLayout]);
  useLayoutEffect(() => { afterLayout(); }, [inLayout, afterLayout]);
  return <section ref={props.sectionRef} aria-label={props.title} className="question-section" style={{ '--section-tint': `var(--${props.tone ?? 'volt'})` } as React.CSSProperties} data-motion-key={`section-${props.id}`}
    data-expanded={inLayout} data-drop-target-id={props.dropTargetId} data-drop-active={props.dropActive || undefined} data-dragging={props.dragging || undefined}>
    <div className="section-heading">
      <div className="section-metadata">
        {props.readOnly ? <><h2 className="section-title">{props.title}</h2>{props.description && <p className="section-description">{props.description}</p>}</> : <>
          <InlineSectionText label="Section title" className="section-title" value={props.title} onChange={value => props.onTitleChange?.(value)} placeholder="Section title" />
          <InlineSectionText label="Section description" className="section-description" value={props.description ?? ''} onChange={value => props.onDescriptionChange?.(value)} placeholder="Description (optional)" />
        </>}
      </div>
      <span className="section-count" aria-live="polite">
        <span>{props.count} {props.count === 1 ? 'question' : 'questions'}</span>
      </span>
      <div className="section-actions">{props.actions}</div>
      <div className="section-controls"><button type="button" className="section-toggle icon-button" aria-label={`Toggle ${props.title}`} aria-expanded={expanded}
        aria-controls={`section-body-${props.id}`} onClick={props.onToggle}>
        <IconChevronDown size={16} className={expanded ? '' : 'section-chevron-closed'} />
      </button>{props.dragHandle}</div>
      {props.headingDropIndicator}
    </div>
    {props.conditions && <div className="section-conditions min-w-0 max-w-full">{props.conditions}</div>}
    <div id={`section-body-${props.id}`} className="section-reveal" hidden={!inLayout} inert={!expanded} aria-hidden={!expanded} data-open={visible}>
      <div className="section-body">{props.children}</div>
    </div>
    {props.dropIndicator}
  </section>;
}
